import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { Gift } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Signup() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');
  const [referrerName, setReferrerName] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  // Validate referral code on load / when it changes
  useEffect(() => {
    const validate = async () => {
      if (referralCode && referralCode.length >= 3) {
        try {
          const { data } = await axios.get(`${API}/referrals/validate/${referralCode}`);
          setReferrerName(data.valid ? data.referrer_name : '');
        } catch {
          setReferrerName('');
        }
      } else {
        setReferrerName('');
      }
    };
    const timer = setTimeout(validate, 400);
    return () => clearTimeout(timer);
  }, [referralCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await signup(email, password, name, referralCode || null);
    setLoading(false);

    if (result.success) {
      if (result.data?.bonus_from_referral) {
        toast.success(`Welcome! You got a bonus 7 days on your trial thanks to ${referrerName || 'your friend'}!`);
      } else {
        toast.success('Welcome! Let\'s get you started.');
      }
      navigate('/questionnaire');
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F3] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <h1 className="text-3xl font-light tracking-tight mb-2" style={{fontFamily: 'Spectral, serif'}}>Stable Stationary</h1>
          </Link>
          <p className="text-sm text-[#737373]">Create your account</p>
        </div>

        {referrerName && (
          <div className="mb-4 bg-[#F5EBE0] border border-[#B94A3E]/30 rounded-md p-4 flex items-center gap-3" data-testid="referral-bonus-banner">
            <Gift className="w-5 h-5 text-[#B94A3E] flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium">{referrerName} invited you!</p>
              <p className="text-[#737373]">You'll get an extra <strong>7 days</strong> added to your free trial.</p>
            </div>
          </div>
        )}

        <div className="bg-white border border-[#E5E0D8] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
          <div className="pt-2">
            <GoogleSignInButton
              label={referrerName ? 'Sign up with Google (claim bonus)' : 'Sign up with Google'}
              referralCode={referralCode || null}
              testId="signup-google-btn"
              variant="promoted"
            />
          </div>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-[#E5E0D8]" />
            <span className="text-xs uppercase tracking-widest text-[#737373]">or use email</span>
            <div className="flex-1 h-px bg-[#E5E0D8]" />
          </div>

          <form onSubmit={handleSubmit} data-testid="signup-form">
            <div className="space-y-4">
              <div>
                <Label htmlFor="name" className="label-text">Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="mt-2 border-[#E5E0D8] focus:ring-[#B94A3E]"
                  data-testid="signup-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email" className="label-text">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-2 border-[#E5E0D8] focus:ring-[#B94A3E]"
                  data-testid="signup-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password" className="label-text">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-2 border-[#E5E0D8] focus:ring-[#B94A3E]"
                  data-testid="signup-password-input"
                />
              </div>
              <div>
                <Label htmlFor="ref" className="label-text">Referral code (optional)</Label>
                <Input
                  id="ref"
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toLowerCase())}
                  placeholder="e.g., sarah-a3f9c1"
                  className="mt-2 border-[#E5E0D8] focus:ring-[#B94A3E]"
                  data-testid="signup-referral-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#B94A3E] hover:bg-[#9d3e33] text-white"
                disabled={loading}
                data-testid="signup-submit-btn"
              >
                {loading ? 'Creating account...' : referrerName ? 'Claim your bonus trial' : 'Create Account'}
              </Button>
            </div>
          </form>

          <div className="mt-6 text-center text-sm">
            <p className="text-[#737373]">
              Already have an account?{' '}
              <Link to="/login" className="text-[#B94A3E] hover:underline" data-testid="signup-login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
